import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Coins, Wallet, Star, MapPin, Sparkles, TrendingUp, Users, Shield, CheckCircle2, ArrowRight, Zap, Gift, Award } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1556745753-b2904692b3cd?w=1600&q=80";

const TIER_STYLE = {
  GRATIS: "border-stone-200 bg-white",
  BRONZE: "border-stone-200 bg-white",
  PRATA:  "border-stone-200 bg-white",
  OURO:   "border-amber-400 shadow-2xl shadow-amber-500/20 -translate-y-2 bg-gradient-to-br from-white to-amber-50",
  PLATINUM: "border-indigo-400 shadow-2xl shadow-indigo-500/20 -translate-y-2 bg-gradient-to-br from-white to-indigo-50",
};

export default function LandingForBusiness() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/payments/plans").then((r) => {
      const arr = (r.data || []).filter((p) => p.active !== false);
      arr.sort((a, b) => (a.search_priority ?? 99) - (b.search_priority ?? 99));
      setPlans(arr);
    }).catch(() => setPlans([]));
  }, []);
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-950 via-blue-900 to-stone-900 text-white">
        <div className="absolute inset-0 opacity-20">
          <img src={HERO} alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-blue-950/85 to-transparent"/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 text-xs font-bold uppercase tracking-widest" data-testid="business-tag">
              <Sparkles size={12}/> Para empresas
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-tight">
              Mais clientes,<br/>mais retorno,<br/><span className="text-amber-400">você decide as regras</span>.
            </h1>
            <p className="text-base sm:text-lg text-blue-100 mt-5 max-w-xl">
              Coloque sua empresa no mapa da cidade e ofereça dois caminhos de fidelização: <strong>Cashback</strong> com a porcentagem que <strong>você define</strong> (usável em toda a rede Morar Bem) ou <strong>Voucher próprio</strong>, válido apenas na sua loja — você decide o valor e a vigência.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register" data-testid="cta-register-business">
                <Button size="lg" className="bg-amber-400 hover:bg-amber-500 text-amber-950 font-bold rounded-xl shadow-lg shadow-amber-900/30 px-6 h-12">
                  Cadastrar minha empresa <ArrowRight size={16} className="ml-1"/>
                </Button>
              </Link>
              <Link to="/pricing" data-testid="cta-see-plans">
                <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-xl px-6 h-12">
                  Ver planos e preços
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-5 mt-8 text-xs text-blue-200">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14}/> Cadastro em minutos</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14}/> Sem fidelidade</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14}/> Pagamento via Mercado Pago</span>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl bg-white p-6 shadow-2xl text-stone-900 border-4 border-amber-400 transform lg:rotate-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Plano Ouro · destaque</div>
              <div className="font-display text-3xl font-bold mt-2">R$ 500 de saldo</div>
              <div className="text-sm text-stone-600 mt-1">de bônus ao assinar para você já começar a distribuir cashback aos clientes.</div>
              <div className="mt-4 pt-4 border-t border-stone-200 grid grid-cols-3 gap-3 text-center text-[11px]">
                <div><Gift className="mx-auto text-amber-600 mb-1" size={20}/>Bônus inicial</div>
                <div><Star className="mx-auto text-amber-600 mb-1" size={20}/>Destaque no mapa</div>
                <div><Award className="mx-auto text-amber-600 mb-1" size={20}/>Topo das buscas</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-blue-900 font-bold">Por que aderir?</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-3">Quatro motivos pra parar de gastar com anúncio que não converte</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Users, title: "Clientes que voltam", desc: "Cashback usável em toda a rede ou Voucher exclusivo da sua loja — você escolhe como atrair de volta." },
            { icon: MapPin, title: "Visibilidade local", desc: "Apareça no mapa da cidade no momento em que o consumidor está procurando o que você vende." },
            { icon: TrendingUp, title: "Ticket médio maior", desc: "Empresas com cashback observam aumento médio de 30% no ticket — o cliente compra mais pra acumular." },
            { icon: Shield, title: "Você no controle", desc: "Defina o % de cashback, valor e vigência dos vouchers. Nenhum centavo a mais é cobrado." },
          ].map((b, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white border border-stone-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={`benefit-${i}`}>
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center mb-4"><b.icon size={22}/></div>
              <h3 className="font-display text-lg font-semibold text-stone-900">{b.title}</h3>
              <p className="text-sm text-stone-600 mt-1.5 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CASHBACK x VOUCHER */}
      <section className="bg-white py-16 sm:py-20 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-[0.25em] text-amber-700 font-bold">Duas armas de fidelização</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-3">Cashback ou Voucher? Use os dois.</h2>
            <p className="text-stone-600 mt-3 text-sm sm:text-base">Os planos liberam soluções diferentes — compre <strong>créditos exclusivos</strong> de cada um e distribua do jeito que faz sentido pro seu negócio.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="rounded-3xl p-6 bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200" data-testid="bf-cashback">
              <div className="flex items-center gap-2">
                <Coins className="text-emerald-700" size={28}/>
                <h3 className="font-display text-2xl font-bold text-stone-900">Cashback</h3>
              </div>
              <p className="text-sm text-stone-600 mt-2">O cliente acumula crédito em <strong>qualquer parceiro da rede Morar Bem</strong> da cidade dele.</p>
              <ul className="mt-4 space-y-2 text-sm text-stone-700">
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0"/><span><strong>Você define o %</strong> de cashback que oferece (não tem valor fixo).</span></li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0"/><span>O cliente <strong>usa onde quiser</strong> da rede — atrai gente nova pra você também.</span></li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0"/><span>Disponível nos planos <strong>Prata, Ouro, Platinum</strong>.</span></li>
              </ul>
            </div>
            <div className="rounded-3xl p-6 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200" data-testid="bf-voucher">
              <div className="flex items-center gap-2">
                <Gift className="text-blue-700" size={28}/>
                <h3 className="font-display text-2xl font-bold text-stone-900">Voucher</h3>
              </div>
              <p className="text-sm text-stone-600 mt-2">O cliente recebe um vale <strong>válido apenas na sua loja</strong> — força o retorno.</p>
              <ul className="mt-4 space-y-2 text-sm text-stone-700">
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 mt-0.5 shrink-0"/><span><strong>Você define o valor e a vigência</strong> de cada voucher.</span></li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 mt-0.5 shrink-0"/><span>Só consumido <strong>na sua loja</strong> — garante o cliente de volta.</span></li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 mt-0.5 shrink-0"/><span>Disponível em <strong>todos os planos pagos</strong> (Bronze, Prata, Ouro, Platinum).</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-blue-950 text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.25em] text-amber-400 font-bold">Como funciona</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Em 4 passos sua empresa já está rodando</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "01", t: "Cadastre", d: "Crie sua conta e a página da empresa em poucos minutos. Use seu CNPJ pra preencher automático." },
              { n: "02", t: "Escolha o plano", d: "Bronze, Prata, Ouro ou Platinum — mensais. Cada um libera Voucher e/ou Cashback." },
              { n: "03", t: "Recarregue créditos", d: "Compre créditos exclusivos pra Voucher ou Cashback via Mercado Pago. Sem taxa extra." },
              { n: "04", t: "Fidelize do seu jeito", d: "Cashback (na rede inteira, % que você define) ou Voucher (só na sua loja, vigência que você define)." },
            ].map((s, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur" data-testid={`step-${i}`}>
                <div className="font-display text-5xl font-bold text-amber-400 leading-none">{s.n}</div>
                <h3 className="font-display text-lg font-semibold mt-3">{s.t}</h3>
                <p className="text-sm text-blue-200 mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLAN COMPARE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-blue-900 font-bold">Planos</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-3">Comece grátis. Cresça quando quiser.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.length === 0 && (
            <p className="col-span-full text-center text-stone-500">Carregando planos...</p>
          )}
          {plans.map((p) => {
            const isOuro = p.tier === "OURO";
            const priceLabel = p.price === 0 ? "Grátis" : `R$ ${p.price.toFixed(2).replace(".", ",")}`;
            const durationLabel = p.unlimited || p.duration_days >= 36500 ? "ilimitado" :
              (p.duration_days >= 30 ? `${Math.round(p.duration_days / 30)} mes${p.duration_days >= 60 ? "es" : ""}` : `${p.duration_days} dias`);
            return (
              <div key={p.tier} className={`p-6 rounded-3xl border-2 ${TIER_STYLE[p.tier] || "border-stone-200 bg-white"}`} data-testid={`plan-card-${p.tier}`}>
                {isOuro && <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-amber-950 mb-3">Mais escolhido</span>}
                <h3 className="font-display text-2xl font-bold text-stone-900">{p.display_name || p.tier}</h3>
                <div className="font-display text-3xl font-bold text-stone-900 mt-2">{priceLabel}{p.price > 0 && <span className="text-sm font-normal text-stone-500"> / {durationLabel}</span>}</div>
                <p className="text-sm text-stone-600 mt-2">{p.description}</p>
                {(p.signup_bonus || 0) > 0 && (
                  <div className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                    <Zap size={11}/> R$ {p.signup_bonus.toFixed(2)} de bônus na carteira
                  </div>
                )}
                <ul className="mt-4 space-y-2 text-sm text-stone-700">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0"/>{f}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link to="/register" data-testid="cta-register-business-2">
            <Button size="lg" className="bg-blue-900 hover:bg-blue-950 text-white font-bold rounded-xl px-8 h-12">
              Começar agora <ArrowRight size={16} className="ml-1"/>
            </Button>
          </Link>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="font-display text-2xl sm:text-3xl font-bold">Sua próxima venda já está procurando por você no mapa.</h3>
          <p className="mt-2 text-sm sm:text-base">Cadastre sua empresa em menos de 2 minutos. É grátis pra começar.</p>
          <Link to="/register" data-testid="cta-register-business-3">
            <Button size="lg" className="mt-5 bg-blue-950 hover:bg-black text-white font-bold rounded-xl px-8 h-12">
              Quero meu cadastro <ArrowRight size={16} className="ml-1"/>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
